import { useCallback, useEffect, useMemo, useState } from "react";
import { FiCheck, FiMapPin, FiX } from "react-icons/fi";
import { useAuth } from "../../../context/AuthContext";
import { apiRequest } from "../../../services/api";

export default function UserBranchAccessModal({
  open,
  onClose,
  user,
  branches,
  stores,
  onChanged,
}) {
  const { accessToken } = useAuth();

  const [accessRows, setAccessRows] = useState([]);
  const [branchId, setBranchId] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const loadAccess = useCallback(async () => {
    if (!user?.id || !accessToken) return;

    try {
      setLoading(true);
      setError("");

      const response = await apiRequest(
        `/user-branch-access/users/${user.id}`,
        { accessToken },
      );

      setAccessRows(response || []);
    } catch (err) {
      setError(err.message || "Unable to load branch access.");
    } finally {
      setLoading(false);
    }
  }, [accessToken, user?.id]);

  useEffect(() => {
    if (!open) return;

    setBranchId("");
    setIsDefault(false);
    loadAccess();
  }, [open, loadAccess]);

  const assignedBranchIds = useMemo(
    () => accessRows.map((item) => item.branchId),
    [accessRows],
  );

  const assignBranch = async () => {
    if (!branchId) {
      setError("Select a branch.");
      return;
    }

    try {
      setSaving(true);
      setError("");

      await apiRequest("/user-branch-access", {
        method: "POST",
        accessToken,
        body: {
          userId: user.id,
          branchId,
          isDefault,
        },
      });

      await loadAccess();
      await onChanged();
      setBranchId("");
      setIsDefault(false);
    } catch (err) {
      setError(err.message || "Unable to assign branch access.");
    } finally {
      setSaving(false);
    }
  };

  const updateAccess = async (access, data) => {
    try {
      setSaving(true);
      setError("");

      await apiRequest(
        `/user-branch-access/users/${user.id}/branches/${access.branchId}`,
        {
          method: "PATCH",
          accessToken,
          body: data,
        },
      );

      await loadAccess();
      await onChanged();
    } catch (err) {
      setError(err.message || "Unable to update branch access.");
    } finally {
      setSaving(false);
    }
  };

  const deactivateAccess = async (access) => {
    const confirmed = window.confirm(
      `Remove ${user.email || user.phone} from ${access.branch.name}?`,
    );

    if (!confirmed) return;

    try {
      setSaving(true);
      setError("");

      await apiRequest(
        `/user-branch-access/users/${user.id}/branches/${access.branchId}/deactivate`,
        {
          method: "POST",
          accessToken,
        },
      );

      await loadAccess();
      await onChanged();
    } catch (err) {
      setError(err.message || "Unable to deactivate branch access.");
    } finally {
      setSaving(false);
    }
  };

  if (!open || !user) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center overflow-y-auto bg-black/40 p-4 backdrop-blur-sm">
      <div className="my-8 w-full max-w-4xl overflow-hidden rounded-3xl bg-white shadow-2xl">
        <div className="flex items-center justify-between border-b border-gray-100 px-6 py-5">
          <div>
            <h2 className="text-2xl font-bold">Branch Assignments</h2>

            <p className="mt-1 text-sm text-gray-500">
              {user.employee?.fullName || user.email || user.phone}
            </p>
          </div>

          <button
            type="button"
            onClick={onClose}
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-gray-100"
          >
            <FiX />
          </button>
        </div>

        <div className="max-h-[75vh] overflow-y-auto p-6">
          {error && (
            <div className="mb-5 rounded-xl bg-red-50 p-4 text-sm text-red-700">
              {error}
            </div>
          )}

          <div className="rounded-2xl bg-primary-50 p-4 text-sm text-primary-800">
            ERP data is visible across all branches and stores according to role
            permissions. These assignments record organizational and default
            branches; they do not restrict data visibility. POS counter
            assignments are managed separately.
          </div>

          <div className="mt-6 rounded-2xl border border-gray-200 p-5">
            <h3 className="font-semibold">Assign Branch</h3>

            <div className="mt-4 grid gap-3 md:grid-cols-[1fr_auto_auto]">
              <select
                value={branchId}
                onChange={(event) => setBranchId(event.target.value)}
                className="h-12 rounded-xl border border-gray-200 px-4"
              >
                <option value="">Select active branch</option>

                {branches
                  .filter(
                    (branch) =>
                      branch.isActive && !assignedBranchIds.includes(branch.id),
                  )
                  .map((branch) => (
                    <option key={branch.id} value={branch.id}>
                      {branch.name} ({branch.code})
                    </option>
                  ))}
              </select>

              <label className="flex h-12 items-center gap-2 rounded-xl border border-gray-200 px-4 text-sm">
                <input
                  type="checkbox"
                  checked={isDefault}
                  onChange={(event) => setIsDefault(event.target.checked)}
                  className="accent-primary-700"
                />
                Default branch
              </label>

              <button
                type="button"
                disabled={saving}
                onClick={assignBranch}
                className="h-12 rounded-xl bg-primary-700 px-5 text-white disabled:opacity-60"
              >
                Assign
              </button>
            </div>
          </div>

          <div className="mt-6">
            <h3 className="mb-4 font-semibold">Assigned Branches</h3>

            {loading ? (
              <p className="text-sm text-gray-500">Loading access…</p>
            ) : accessRows.length === 0 ? (
              <p className="rounded-xl bg-gray-50 p-5 text-sm text-gray-500">
                No branch access assigned yet.
              </p>
            ) : (
              <div className="space-y-4">
                {accessRows.map((access) => {
                  const branchStores = stores.filter(
                    (store) =>
                      store.branchId === access.branchId &&
                      store.deletedAt === null &&
                      store.isActive,
                  );

                  return (
                    <div
                      key={access.id}
                      className={`rounded-2xl border p-5 ${
                        access.isActive
                          ? "border-gray-200"
                          : "border-gray-100 bg-gray-50 opacity-70"
                      }`}
                    >
                      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                        <div>
                          <div className="flex flex-wrap items-center gap-2">
                            <h4 className="font-semibold text-gray-900">
                              {access.branch.name}
                            </h4>

                            {access.isDefault && (
                              <span className="rounded-full bg-primary-50 px-3 py-1 text-xs font-medium text-primary-700">
                                Default
                              </span>
                            )}

                            {!access.isActive && (
                              <span className="rounded-full bg-gray-200 px-3 py-1 text-xs font-medium text-gray-700">
                                Inactive
                              </span>
                            )}
                          </div>

                          <p className="mt-1 text-sm text-gray-500">
                            Branch code: {access.branch.code}
                          </p>
                        </div>

                        <div className="flex flex-wrap gap-2">
                          {!access.isDefault && access.isActive && (
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() =>
                                updateAccess(access, {
                                  isDefault: true,
                                  isActive: true,
                                })
                              }
                              className="rounded-xl border border-primary-200 px-4 py-2 text-sm text-primary-700"
                            >
                              Make Default
                            </button>
                          )}

                          {access.isActive && (
                            <button
                              type="button"
                              disabled={saving}
                              onClick={() => deactivateAccess(access)}
                              className="rounded-xl border border-red-200 px-4 py-2 text-sm text-red-600"
                            >
                              Remove Access
                            </button>
                          )}
                        </div>
                      </div>

                      <div className="mt-5 border-t border-gray-100 pt-4">
                        <p className="mb-3 text-sm font-medium text-gray-700">
                          Inherited Stores & Warehouses
                        </p>

                        {branchStores.length === 0 ? (
                          <p className="text-sm text-gray-500">
                            No active stores are linked to this branch.
                          </p>
                        ) : (
                          <div className="flex flex-wrap gap-2">
                            {branchStores.map((store) => (
                              <span
                                key={store.id}
                                className="flex items-center gap-2 rounded-full bg-gray-100 px-3 py-2 text-xs text-gray-700"
                              >
                                <FiMapPin />
                                {store.name} · {store.type}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
